import React, { useCallback, useEffect, useState } from 'react';
import {
  acApi,
  type AcAssessmentRow,
  type AcConfig,
  type AcHistoryRow,
  type AcImportPreview,
  type CriterionKey,
  type Scores,
} from './ac-api.js';
import { useSession } from './session.js';
import { useConfirm, useToasts, EmptyState, Skeleton, ErrorState } from './shell.js';
import { useSync } from './sync.js';

const CRITERIA: readonly CriterionKey[] = [
  'safety',
  'environment',
  'production',
  'detectability',
  'regulatory',
] as const;
const CRITERION_LABEL: Record<CriterionKey, string> = {
  safety: 'H&S',
  environment: 'Env',
  production: 'Prod',
  detectability: 'Det',
  regulatory: 'Reg',
};

export function AssetCriticality({ siteId }: { siteId: string }) {
  const sess = useSession();
  const [tab, setTab] = useState<'assess' | 'config' | 'history' | 'import'>('assess');
  return (
    <section aria-label="Asset Criticality" data-testid="ac-workspace">
      <h2>Asset Criticality</h2>
      <div className="edv-tabs" role="tablist" aria-label="AC tabs">
        <button aria-selected={tab === 'assess'} role="tab" onClick={() => setTab('assess')} data-testid="ac-tab-assess">
          Assess
        </button>
        <button aria-selected={tab === 'config'} role="tab" onClick={() => setTab('config')} data-testid="ac-tab-config">
          Configure
        </button>
        <button aria-selected={tab === 'history'} role="tab" onClick={() => setTab('history')} data-testid="ac-tab-history">
          History
        </button>
        <button aria-selected={tab === 'import'} role="tab" onClick={() => setTab('import')} data-testid="ac-tab-import">
          Import
        </button>
      </div>
      {sess.me ? (
        <>
          {tab === 'assess' && <AssessPane siteId={siteId} deviceId={sess.me.device.id} />}
          {tab === 'config' && <ConfigPane siteId={siteId} deviceId={sess.me.device.id} />}
          {tab === 'history' && <HistoryPane siteId={siteId} />}
          {tab === 'import' && <ImportPane siteId={siteId} deviceId={sess.me.device.id} />}
        </>
      ) : (
        <div>Loading session…</div>
      )}
    </section>
  );
}

function useMaintenanceGuard() {
  const sync = useSync();
  const readonly = sync.state === 'auth_required' || sync.state === 'failed';
  return { readonly, state: sync.state };
}

function AssessPane({ siteId, deviceId }: { siteId: string; deviceId: string }) {
  const [list, setList] = useState<{ data: AcAssessmentRow[]; cursor: string | null } | null>(null);
  const [selected, setSelected] = useState<AcAssessmentRow | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const guard = useMaintenanceGuard();

  const load = useCallback(
    async (cursor?: string) => {
      try {
        const options = cursor ? { cursor, limit: 25 } : { limit: 25 };
        setList(await acApi.listAssessments(siteId, options));
      } catch (e) {
        setErr((e as Error).message);
      }
    },
    [siteId],
  );
  useEffect(() => {
    setSelected(null);
    setList(null);
    void load();
  }, [load]);
  return (
    <div>
      {guard.readonly && <p role="status">Read-only ({guard.state})</p>}
      {err && <p role="alert">{err}</p>}
      <table aria-label="AC Assessments">
        <thead>
          <tr>
            <th>Asset</th>
            <th>Ranking</th>
            <th>Score</th>
            <th>Complete</th>
            <th>Status</th>
            <th>V</th>
          </tr>
        </thead>
        <tbody>
          {(list?.data ?? []).map((a) => (
            <tr
              key={a.asset_id}
              onClick={() => setSelected(a)}
              aria-label={`Assessment ${a.asset_id}`}
            >
              <td>
                <button onClick={() => setSelected(a)}>{a.asset_id.slice(0, 8)}</button>
              </td>
              <td>{a.ranking ?? '—'}</td>
              <td>{a.score ?? '—'}</td>
              <td>{a.complete ? 'Yes' : 'No'}</td>
              <td>{a.status}</td>
              <td>{a.version}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {list?.cursor && <button onClick={() => load(list.cursor!)}>Next page</button>}
      {selected && (
        <AssessDetail
          row={selected}
          siteId={siteId}
          deviceId={deviceId}
          onClose={() => setSelected(null)}
          onChange={() => load()}
          readonly={guard.readonly}
        />
      )}
    </div>
  );
}

function AssessDetail({
  row,
  siteId,
  deviceId,
  onClose,
  onChange,
  readonly,
}: {
  row: AcAssessmentRow;
  siteId: string;
  deviceId: string;
  onClose: () => void;
  onChange: () => void;
  readonly: boolean;
}) {
  const [scores, setScores] = useState<Scores>(row.scores || {});
  const [justification, setJustification] = useState(row.justification || '');
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<unknown>(null);
  const [copyFrom, setCopyFrom] = useState<string>('');
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    setScores(row.scores || {});
    setJustification(row.justification || '');
  }, [row]);

  const updateScore = (k: CriterionKey, which: 'c' | 'l', v: number | null) => {
    setScores((prev) => ({ ...prev, [k]: { ...(prev[k] || {}), [which]: v } }));
  };

  const doPreview = async () => {
    try {
      setPreview(await acApi.calcPreview({ scores, justification }));
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  const save = async () => {
    try {
      await acApi.saveAssessment(row.asset_id, deviceId, row.version, { scores, justification });
      onChange();
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  const clear = async () => {
    try {
      await acApi.clearScores(row.asset_id, deviceId, row.version);
      onChange();
      setConfirmClear(false);
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  const copy = async () => {
    if (!copyFrom || copyFrom === row.asset_id) return;
    try {
      await acApi.copyScores(row.asset_id, copyFrom, deviceId, row.version);
      onChange();
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  return (
    <aside aria-label="Assessment detail">
      <button className="btn" onClick={onClose} data-testid="ac-detail-close">Close</button>
      <h3>Assessment {row.asset_id.slice(0, 8)}</h3>
      <table>
        <thead>
          <tr>
            <th>Criterion</th>
            <th>C (1-5)</th>
            <th>L (1-5)</th>
          </tr>
        </thead>
        <tbody>
          {CRITERIA.map((k) => (
            <tr key={k}>
              <td>{CRITERION_LABEL[k]}</td>
              <td>
                <input
                  type="number"
                  min={1}
                  max={5}
                  value={scores[k]?.c ?? ''}
                  onChange={(e) =>
                    updateScore(k, 'c', e.target.value ? Number(e.target.value) : null)
                  }
                  disabled={readonly}
                  aria-label={`${k} c`}
                />
              </td>
              <td>
                <input
                  type="number"
                  min={1}
                  max={5}
                  value={scores[k]?.l ?? ''}
                  onChange={(e) =>
                    updateScore(k, 'l', e.target.value ? Number(e.target.value) : null)
                  }
                  disabled={readonly}
                  aria-label={`${k} l`}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <label>
        Justification{' '}
        <textarea
          value={justification}
          onChange={(e) => setJustification(e.target.value)}
          disabled={readonly}
        />
      </label>
      <button onClick={doPreview}>Preview score</button>
      <button onClick={save} disabled={readonly}>
        Save
      </button>
      <button onClick={() => setConfirmClear(true)} disabled={readonly}>
        Clear scores…
      </button>
      <div>
        <input
          placeholder="Source asset id to copy from"
          value={copyFrom}
          onChange={(e) => setCopyFrom(e.target.value)}
          aria-label="copy source"
        />
        <button onClick={copy} disabled={readonly || !copyFrom}>
          Copy scores
        </button>
      </div>
      {confirmClear && (
        <div role="dialog" aria-label="Confirm clear">
          <p>Clear all C/L scores for this asset? Justifications will be kept.</p>
          <button onClick={clear}>Yes, clear</button>
          <button onClick={() => setConfirmClear(false)}>Cancel</button>
        </div>
      )}
      {preview !== null && <pre aria-label="Score preview">{JSON.stringify(preview, null, 2)}</pre>}
      {err && <p role="alert">{err}</p>}
    </aside>
  );
}

function ConfigPane({ siteId, deviceId }: { siteId: string; deviceId: string }) {
  const [cfg, setCfg] = useState<AcConfig | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    acApi
      .getConfig(siteId)
      .then(setCfg)
      .catch((e) => setErr((e as Error).message));
  }, [siteId]);
  if (!cfg) return <p>Loading config…</p>;
  const save = async () => {
    try {
      await acApi.saveConfig(siteId, deviceId, cfg?.version ?? null, {
        thresholds: cfg.thresholds,
        safetyOverride: { enabled: true, cThreshold: cfg.safety_override_c_threshold ?? 5 },
      });
      alert('saved');
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  return (
    <div>
      <p>Version: {cfg.version ?? 0}</p>
      <p>
        Safety override C threshold:{' '}
        <input
          type="number"
          min={1}
          max={5}
          value={cfg.safety_override_c_threshold ?? 5}
          onChange={(e) => setCfg({ ...cfg, safety_override_c_threshold: Number(e.target.value) })}
        />
      </p>
      <p>
        Thresholds: <code>{JSON.stringify(cfg.thresholds)}</code>
      </p>
      <button onClick={save}>Save config</button>
      {err && <p role="alert">{err}</p>}
    </div>
  );
}

function HistoryPane({ siteId }: { siteId: string }) {
  const [rows, setRows] = useState<AcHistoryRow[]>([]);
  useEffect(() => {
    acApi
      .history(siteId)
      .then((r) => setRows(r.data))
      .catch(() => {});
  }, [siteId]);
  return (
    <ul aria-label="AC history">
      {rows.map((h) => (
        <li key={h.id}>
          {new Date(h.at).toISOString()} · {h.action} · {h.asset_id.slice(0, 8)}
        </li>
      ))}
    </ul>
  );
}

function ImportPane({ siteId, deviceId }: { siteId: string; deviceId: string }) {
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<AcImportPreview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const parseCsv = (): Array<{ eam_id: string; scores: Scores }> => {
    return csv
      .split(/\r?\n/)
      .filter(Boolean)
      .map((line) => {
        const [eam_id, saC, saL, envC, envL, prC, prL, detC, detL, regC, regL] = line.split(',');
        const num = (v?: string) => (v && v.trim() ? Number(v) : null);
        return {
          eam_id: eam_id?.trim() ?? '',
          scores: {
            safety: { c: num(saC), l: num(saL) },
            environment: { c: num(envC), l: num(envL) },
            production: { c: num(prC), l: num(prL) },
            detectability: { c: num(detC), l: num(detL) },
            regulatory: { c: num(regC), l: num(regL) },
          },
        };
      });
  };
  const doPreview = async () => {
    try {
      setPreview(await acApi.importPreview(siteId, parseCsv()));
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  const doApply = async () => {
    if (!preview) return;
    try {
      const accepted = preview.update.concat(preview.create).map((r) => {
        const row = parseCsv()[r.row_index]!;
        return {
          row_index: r.row_index,
          asset_id: r.asset_id,
          scores: row.scores,
          justification: '',
        };
      });
      const r = await acApi.importApply(siteId, deviceId, accepted);
      setPreview({ ...preview, applied: r });
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  return (
    <div>
      <p>
        Paste CSV: <code>eam_id, S.C, S.L, E.C, E.L, P.C, P.L, D.C, D.L, R.C, R.L</code>
      </p>
      <textarea rows={6} value={csv} onChange={(e) => setCsv(e.target.value)} aria-label="AC CSV" />
      <button onClick={doPreview}>Preview</button>
      {preview && (
        <>
          <button onClick={doApply}>Apply</button>
          <pre>{JSON.stringify(preview, null, 2)}</pre>
        </>
      )}
      {err && <p role="alert">{err}</p>}
    </div>
  );
}
